"use client";

import React from "react";
import { Activity, OctagonAlert as AlertOctagon, ArrowRight, CircleCheck as CheckCircle2, Droplets, Pipette, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  id: "drain-cleaning",
  title: "Drain Cleaning",
  icon: Droplets,
  badge: "Fast Response",
  summary: "Clear buildup, hair, soap scum, and food debris before complete pipe blockage occurs.",
  symptom1: "Slow-draining sinks or tubs",
  symptom2: "Gurgling sounds in pipes",
  symptom3: "Foul odor around drain openings",
  ctaLabel: "Triage Drain Issue",
  linkPath: "/drain-cleaning/"
}, {
  id: "clogged-drain",
  title: "Clogged Drain Service",
  icon: Pipette,
  badge: "Urgent Care",
  summary: "Heavy-duty snaking and hydro-jetting to eliminate persistent standing water clogs.",
  symptom1: "Water backing up immediately",
  symptom2: "Standing water in shower or sink",
  symptom3: "Multiple fixtures draining poorly",
  ctaLabel: "Clear My Drain",
  linkPath: "/drain-cleaning/"
}, {
  id: "sewer-repair",
  title: "Sewer Line Repair",
  icon: Wrench,
  badge: "Advanced Diagnostic",
  summary: "Precision camera inspections, trenchless relining, and underground pipe replacements.",
  symptom1: "Lush, soggy patches in yard",
  symptom2: "Sewage smells indoors or outside",
  symptom3: "Foundation cracks or shifting soil",
  ctaLabel: "Diagnose Sewer Line",
  linkPath: "/sewer-line-repair/"
}, {
  id: "sewer-backup",
  title: "Sewer Backup Service",
  icon: Waves,
  badge: "Emergency Priority",
  summary: "Immediate containment and relief for hazardous main line sewer backflow emergencies.",
  symptom1: "Toilets overflowing when run",
  symptom2: "Dirty wastewater in basement drains",
  symptom3: "Simultaneous backup across house",
  ctaLabel: "Resolve Backup Now",
  linkPath: "/emergency-plumbing/"
}];
export default function DrainServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="drainsewerservicescluster" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold text-xs tracking-wide uppercase border border-border bg-secondary text-secondary-foreground">
            <AlertOctagon className="size-3.5 text-accent-foreground" />
            Specialized Drain & Sewer Solutions
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Stop Backups Before They Flood Your Property
          </h2>
          
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Identify your symptom, isolate the pipe hazard, and connect with our 24/7 Jonesboro technicians for rapid dispatch.
          </p>
        </div>

        {/* 4-Card Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 24
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.45,
            delay: index * 0.1,
            ease: "easeOut"
          }} className="h-full flex">
                <Card className="flex-1 flex flex-col justify-between border border-border/80 bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-300 rounded-2xl group overflow-hidden">
                  <div>
                    <CardHeader className="pb-3 pt-6 px-6">
                      <div className="flex items-center justify-between gap-2 mb-4">
                        {/* Soft Squircle Icon Pod */}
                        <div className="size-13 rounded-2xl bg-secondary text-primary flex items-center justify-center p-3 border border-border/50 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                          <Icon className="size-6 shrink-0" />
                        </div>
                        <Badge variant="outline" className="text-[11px] font-medium border-border/80 text-muted-foreground bg-background">
                          {service.badge}
                        </Badge>
                      </div>

                      <CardTitle className="text-xl font-default font-bold text-card-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 space-y-4">
                      <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                        {service.summary}
                      </p>

                      <div className="pt-2 border-t border-border/50">
                        <span className="text-xs font-semibold uppercase tracking-wider text-card-foreground/80 flex items-center gap-1 mb-2.5">
                          <Activity className="size-3 text-accent-foreground" />
                          Common Symptoms
                        </span>
                        <ul className="space-y-2 text-xs text-muted-foreground font-sans">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                            <span>{service.symptom1}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                            <span>{service.symptom2}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                            <span>{service.symptom3}</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6 px-6">
                    <Button asChild variant="default" className="w-full justify-between rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium group-hover:shadow-sm">
                      <Link to={service.linkPath}>
                        <span>{service.ctaLabel}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}